// Param-summary renderer for the block status bar. Spec (Task 4.2):
//   v2 → "Stab 50% · Sim 75% · Sty 0% · Boost: on"
//   v4 → "Stab 50% · Sim 75%"
//   v3 → "Mode: natural · Boost: on" (nur noch Alt-Takes, vor 2026-09-29)
// Duration, if supplied, gets appended as "3.2s" after a " · " separator.
//
// STS variant mirrors vanilla `getStsParamSummary` (legacy/index.html line
// 9410-9412) verbatim — used in the STS take-summary line under each
// waveform. Format string in vanilla:
//   `Stab N% · Sim N% · Style N%[· Boost][· Denoise]`
// Note the full word "Style" (not "Sty") and the conditional Boost/Denoise
// suffixes that only appear when the toggle is ON.

import type { STSParams, TtsParamsV2, TtsParamsV3, TtsParamsV4 } from '@shared/types';

export function formatV2Summary(p: TtsParamsV2): string {
  const stab = Math.round((p.stability ?? 0) * 100);
  const sim = Math.round((p.similarity ?? 0) * 100);
  const sty = Math.round((p.style ?? 0) * 100);
  const boost = p.speakerBoost ? 'on' : 'off';
  return `Stab ${stab}% · Sim ${sim}% · Sty ${sty}% · Boost: ${boost}`;
}

export function formatV4Summary(p: TtsParamsV4): string {
  const stab = Math.round((p.stability ?? 0) * 100);
  const sim = Math.round((p.similarity ?? 0) * 100);
  return `Stab ${stab}% · Sim ${sim}%`;
}

/** Alt-Takes aus Eleven v3 (Sessions vor 2026-09-29). */
export function formatV3Summary(p: TtsParamsV3): string {
  const boost = p.speakerBoost ? 'on' : 'off';
  return `Mode: ${p.stabilityMode} · Boost: ${boost}`;
}

export function formatDuration(seconds: number): string {
  return `${seconds.toFixed(1)}s`;
}

export function getParamSummary(
  paramsSnapshot: TtsParamsV2 | TtsParamsV4 | TtsParamsV3,
  duration?: number,
): string {
  // Unterscheidung über die Form: v3 hat `stabilityMode`, v2 `style`.
  const base =
    'stabilityMode' in paramsSnapshot
      ? formatV3Summary(paramsSnapshot)
      : 'style' in paramsSnapshot
        ? formatV2Summary(paramsSnapshot)
        : formatV4Summary(paramsSnapshot);
  if (typeof duration === 'number' && duration > 0) {
    return `${base} · ${formatDuration(duration)}`;
  }
  return base;
}

/**
 * STS-variant of the one-line param summary.
 *
 * Vanilla source (legacy/index.html:9410-9412):
 *   return `Stab ${Math.round(params.stability*100)}% · Sim ${Math.round(
 *     params.similarity*100)}% · Style ${Math.round(params.style*100)}%${
 *     params.speakerBoost ? ' · Boost' : ''}${
 *     params.removeNoise ? ' · Denoise' : ''}`;
 *
 * Differences from the TTS summary:
 *  - Third field reads "Style N%" (full word, vanilla parity) — not "Sty N%".
 *  - Boolean toggles (Boost, Denoise) are appended only when ON; their
 *    absence implies off (no "Boost: off" suffix like the TTS line).
 *  - Duration is appended via the same " · {n.n}s" convention when supplied,
 *    matching how the React TTS path extends its base summary. (Vanilla's
 *    helper takes only `params` and never appends duration — the duration
 *    suffix is a React-only convenience for the take-summary line.)
 */
export function getStsParamSummary(
  params: STSParams,
  duration?: number,
): string {
  const pct = (v: number) => `${Math.round((v ?? 0) * 100)}%`;
  let base =
    `Stab ${pct(params.stability)} · Sim ${pct(params.similarity)} · Style ${pct(params.style)}`;
  if (params.speakerBoost) base += ' · Boost';
  if (params.removeNoise) base += ' · Denoise';
  if (typeof duration === 'number' && duration > 0) {
    return `${base} · ${formatDuration(duration)}`;
  }
  return base;
}
